import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ReadUsersUsersGetResponse } from "@/client-admin/types.gen.ts";
import { DataTableColumnHeader } from "./data-table-column-header";
import { EditUserDialog } from "./EditUserDialog";
import { ResendInviteDialog } from "./ResendInviteDialog";

export type UserWithClients = ReadUsersUsersGetResponse["data"][number];

export const columns: ColumnDef<UserWithClients>[] = [
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => <div>{row.getValue("email")}</div>,
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Active" />,
    cell: ({ row }) => <div>{row.getValue("is_active") ? "Yes" : "No"}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "last_login_at",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Login" />,
    cell: ({ row }) => {
      const lastLogin = row.getValue("last_login_at");

      if (typeof lastLogin != "string") return <ResendInviteDialog userId={row.original.id} />;

      const date = new Date(lastLogin.endsWith("Z") ? lastLogin : `${lastLogin}Z`);

      return <div>{format(date, "yyyy-MM-dd:HH:mm")}</div>;
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <div className="flex items-center justify-center">
        <DataTableColumnHeader column={column} title="Created At" />
      </div>
    ),
    cell: ({ row }) => {
      const createdAt = row.getValue("created_at");
      if (typeof createdAt !== "string") return <div>-</div>;

      const date = new Date(createdAt.endsWith("Z") ? createdAt : `${createdAt}Z`);

      return <div>{format(date, "yyyy-MM-dd:HH:mm")}</div>;
    },
  },
  {
    accessorKey: "updated_at",
    header: ({ column }) => (
      <div className="flex items-center justify-center">
        <DataTableColumnHeader column={column} title="Updated At" />
      </div>
    ),
    cell: ({ row }) => {
      const updatedAt = row.getValue("updated_at");
      return updatedAt ? format(new Date(updatedAt as string), "yyyy-MM-dd HH:mm:ss") : "-";
    },
  },
  {
    accessorKey: "client_links",
    header: "Client Links",
    cell: ({ row }) => {
      const clients = row.original.clients;

      if (clients.length === 0) return <div>-</div>;

      if (clients.length <= 2) {
        return <div>{clients.map((client) => client.client_name).join(", ")}</div>;
      }

      return (
        <Popover>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-2 hover:underline">
              <span className="max-w-[150px] truncate">{clients[0].client_name}</span>
              <Badge variant="secondary" className="cursor-pointer">
                +{clients.length - 1}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">All Clients ({clients.length})</h4>
              <div className="max-h-[200px] space-y-1 overflow-y-auto">
                {clients.map((client) => (
                  <div key={client.id} className="py-1 text-sm">
                    {client.client_name}
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      );
    },
    filterFn: (row, _, value: number) => {
      if (!value) return true;
      return row.original.clients.some((client) => client.id === value);
    },
    size: 200,
  },
  {
    accessorKey: "first_name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="First Name" />,
    cell: ({ row }) => <div>{row.getValue("first_name") || "-"}</div>,
  },
  {
    accessorKey: "last_name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Name" />,
    cell: ({ row }) => <div>{row.getValue("last_name") || "-"}</div>,
  },
  {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <EditUserDialog
        key={`${row.original.id}-${new Date().getTime()}`}
        id={row.original.id}
        email={row.original.email}
        client_links={row.original.client_links}
        first_name={row.original.first_name}
        last_name={row.original.last_name}
        is_active={row.original.is_active}
      />
    ),
  },
];
